import React, { useState, useEffect, useRef } from 'react';
import { Minus, Plus } from 'lucide-react';

interface QtyInputProps {
  value: number;
  onChange: (qty: number) => void;
  min?: number;
  max?: number;
  /** Incremento per i bottoni ± e multiplo di riferimento per l'input manuale. Default: 1. */
  step?: number;
  size?: 'sm' | 'md';
  onExceedMax?: (qty: number) => void;
  /** Chiamato quando l'input manuale viene arrotondato al multiplo inferiore. */
  onRounded?: (from: number, to: number) => void;
  /** Se true, arrotonda per eccesso invece che per difetto. Default: false. */
  roundUp?: boolean;
}

export function QtyInput({ value, onChange, min = 1, max = 999, step = 1, size = 'md', onExceedMax, onRounded, roundUp = false }: QtyInputProps) {
  const [localVal, setLocalVal] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalVal(String(value));
  }, [value]);

  /** Arrotonda n al multiplo di step più vicino per difetto/eccesso, mai sotto min. */
  const snap = (n: number, roundUp = false): number => {
    if (step <= 1) return Math.max(min, n);
    const rounded = roundUp
      ? Math.ceil(n / step) * step
      : Math.floor(n / step) * step;
    return Math.max(min, rounded);
  };

  // Evita doppio commit quando Enter chiama blur subito dopo
  const justCommittedRef = useRef(false);

  const commit = (raw: string) => {
    const num = parseInt(raw, 10);
    if (isNaN(num) || num < min) {
      onChange(min);
      setLocalVal(String(min));
    } else {
      const snapped = snap(num, roundUp);
      if (snapped !== num && onRounded) onRounded(num, snapped);
      if (snapped > max) {
        // Oltre il massimo (di norma la disponibilità): chi passa
        // ``onExceedMax`` decide lui cosa fare del valore digitato (es. la
        // scheda prodotto mostra StockWarning); senza, si ferma al massimo.
        // Prima il valore passava comunque a ``onChange``: digitando si
        // superava la giacenza, i bottoni ± invece si fermavano.
        if (onExceedMax) {
          onExceedMax(snapped);
          setLocalVal(String(snapped));
        } else {
          const capped = Math.max(min, max);
          onChange(capped);
          setLocalVal(String(capped));
        }
        return;
      }
      onChange(snapped);
      setLocalVal(String(snapped));
    }
  };

  const handleBlur = () => {
    if (justCommittedRef.current) { justCommittedRef.current = false; return; }
    commit(localVal);
  };
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      justCommittedRef.current = true;
      commit(localVal);
      inputRef.current?.blur();
    }
  };

  const isSm = size === 'sm';
  /* Modificare la quantità è l'operazione più delicata del carrello ed
     era quella con l'area di tocco più piccola: 26px su mobile, a pochi
     pixel dal campo. Sotto lg si sale a 44px, da lg in su restano le
     dimensioni originali per non muovere il desktop. */
  const btnCls = isSm
    ? 'w-11 h-11 lg:w-6 lg:h-6 flex items-center justify-center hover:bg-secondary/60 transition-colors'
    : 'w-11 h-11 lg:w-8 lg:h-8 flex items-center justify-center hover:bg-secondary/60 transition-colors';
  const inputCls = isSm
    ? 'w-14 h-11 lg:w-10 lg:h-6 text-center text-sm lg:text-xs font-medium border-x bg-white'
    : 'w-14 h-11 lg:w-12 lg:h-8 text-center text-sm font-medium border-x bg-white';

  return (
    <div className="flex items-center rounded-lg border" data-testid="qty-input">
      <button
        onClick={() => onChange(Math.max(min, value - step))}
        className={`${btnCls} rounded-l-lg`}
        disabled={value <= min}
        aria-label="Riduci quantità"
      >
        <Minus className={isSm ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      </button>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={localVal}
        onChange={e => setLocalVal(e.target.value.replace(/\D/g, ''))}
        onBlur={handleBlur}
        onKeyDown={handleKey}
        onFocus={e => e.target.select()}
        className={`${inputCls} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:outline-none focus:bg-blue-50 tabular-nums`}
        aria-label="Quantità"
      />
      <button
        onClick={() => {
          const next = Math.min(value + step, max);
          onChange(next);
          if (next >= max && onExceedMax) onExceedMax(next);
        }}
        className={`${btnCls} rounded-r-lg`}
        disabled={value >= max}
        aria-label="Aumenta quantità"
      >
        <Plus className={isSm ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      </button>
    </div>
  );
}
